import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { AlertTriangle, TrendingUp, HelpCircle } from 'lucide-react';

const icons = [AlertTriangle, TrendingUp, HelpCircle];

const WhyUs = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { t } = useLanguage();

  const faqItems = t('whyFaq', []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="kapec" className="py-24 section-bark relative overflow-hidden" ref={sectionRef} data-testid="why-us-section">
      <div className="absolute inset-0 forest-pattern opacity-20" />
      <div className="absolute inset-0 forest-grain opacity-40" />
      <div className="absolute top-10 right-10 w-72 h-72 bg-green-500/10 rounded-full blur-3xl animate-blob" />
      <div className="absolute bottom-10 left-10 w-80 h-80 bg-green-400/10 rounded-full blur-3xl animate-blob" style={{ animationDelay: '3s' }} />
      <div className="absolute top-24 left-1/3 w-44 h-2 forest-branch animate-sway" style={{ animationDelay: '1s' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left Content */}
          <div className={`lg:sticky lg:top-32 transition-all duration-700 text-center lg:text-left ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '0.1s' }}>
            <span className="inline-block text-green-400 font-medium text-sm tracking-wider uppercase mb-4">
              {t('why.eyebrow', 'Izglītojoša informācija')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              {t('why.title', '')}
            </h2>
            <p className="text-white/80 text-lg mb-6 leading-relaxed max-w-2xl mx-auto lg:mx-0">
              {t('why.description', '')}
            </p>
            <p className="text-white/70 text-base mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0">
              {t('why.conclusion', '')}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 justify-items-center lg:justify-items-start">
              {(t('why.stats', [])).map((stat, index) => (
                <div key={`${stat.value}-${index}`} className="forest-panel-dark rounded-xl p-6 hover-lift transition-all text-center lg:text-left">
                  <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                  <p className="text-white/60 text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Accordion */}
          <div className={`forest-panel rounded-2xl p-6 sm:p-8 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '0.2s' }}>
            <Accordion type="single" collapsible className="w-full space-y-4">
              {faqItems.map((item, index) => {
                const IconComponent = icons[index % icons.length];
                return (
                  <AccordionItem
                    key={item.id}
                    value={`item-${item.id}`}
                    className="border border-gray-100 rounded-xl px-4 data-[state=open]:border-[#1F3D2B]/30 transition-colors"
                  >
                    <AccordionTrigger className="hover:no-underline py-4">
                      <div className="flex items-center gap-3 text-left">
                        <div className="w-10 h-10 rounded-lg bg-[#1F3D2B]/10 flex items-center justify-center flex-shrink-0">
                          <IconComponent className="w-5 h-5 text-[#1F3D2B]" />
                        </div>
                        <span className="font-semibold text-[#2E2E2E] text-base">
                          {item.title}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-[#2E2E2E]/70 pb-4 pl-13">
                      <div className="pl-13">
                        {item.content}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>

            {/* CTA */}
            <div className="mt-8 p-4 forest-panel rounded-xl">
              <p className="text-[#2E2E2E]/70 text-sm mb-3">
                {t('why.ctaTitle', 'Vēlaties uzzināt vairāk par cirsmu vērtēšanu?')}
              </p>
              <a
                href="#kontakti"
                className="inline-flex items-center gap-2 px-4 py-2 forest-cta text-sm font-semibold transition-all"
              >
                {t('why.ctaButton', 'Sazinieties ar mums')}
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyUs;
